import { createLocalIssues } from "./issues-local";
import {
  FilesError,
  type ProjectFile,
  type ProjectFileVersion,
  type ProjectFilesApi,
  type ProjectFolder,
  type TrashedFile,
  type Upload,
} from "./types";

/**
 * Project files kept in this browser, for local mode (`VITE_SYNC_MODE=local`, where drawings live here too): the same
 * behaviour as the doc service's (a name again is the next version, names compare without case, every version
 * kept, a deleted file kept in the Trash until its time is up, its name free meanwhile), over a small store: IndexedDB
 * in the app (`local-store.ts`), memory in tests. With no purge job here, files whose time is up go when the Trash is
 * next listed.
 */

/** One version as stored: its details and the key of its bytes. */
export interface StoredVersion extends ProjectFileVersion {
  readonly blob: string;
}

export interface StoredFile {
  readonly id: string;
  readonly projectId: string;
  readonly name: string;
  /** Every version, the latest first. */
  readonly versions: readonly StoredVersion[];
  /** ISO 8601: when its latest version arrived. */
  readonly updatedAt: string;
  /** The folder it is in; none or null at the top level. */
  readonly folder?: string | null;
  /** Set while it is in the Trash: when it was deleted, by whom, and when it goes for good (ISO 8601). */
  readonly deletedAt?: string | undefined;
  readonly deletedBy?: string | undefined;
  readonly purgeAfter?: string | undefined;
}

/** Where local files are kept. */
export interface LocalStore {
  files(projectId: string): Promise<StoredFile[]>;
  putFile(file: StoredFile): Promise<void>;
  blob(key: string): Promise<Uint8Array | undefined>;
  putBlob(key: string, bytes: Uint8Array): Promise<void>;
  folders(projectId: string): Promise<ProjectFolder[]>;
  putFolders(projectId: string, folders: readonly ProjectFolder[]): Promise<void>;
  deleteFile(id: string): Promise<void>;
  deleteBlob(key: string): Promise<void>;
}

export interface LocalFilesOptions {
  readonly store: LocalStore;
  readonly maxUploadBytes: number;
  /** How long a deleted file stays in the Trash (`VITE_FILES_TRASH_RETENTION_DAYS`). */
  readonly trashRetentionMs: number;
  readonly now: () => Date;
  readonly newId: () => string;
}

/** What DWG asks for in local mode: the doc service converts it (WS-06; D-029: acadrust never runs in the browser). */
export const DWG_NEEDS_SERVICE =
  "DWG files open and save through the project's files on the server. This browser keeps drawings as DXF only.";

/** The longest file name, as the doc service allows (`files/names.rs`). */
export const MAX_NAME_CHARS = 255;

/** A name as the doc service takes it, trimmed, or why not. */
export function checkName(name: string): string {
  const trimmed = name.trim();
  if (trimmed === "") throw new FilesError("bad_request", "A file needs a name.");
  if ([...trimmed].length > MAX_NAME_CHARS)
    throw new FilesError(
      "bad_request",
      `A file name is at most ${String(MAX_NAME_CHARS)} characters.`,
    );
  if (trimmed === "." || trimmed === "..")
    throw new FilesError("bad_request", `"${trimmed}" is not a file name.`);
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  if (/[/\\\u0000-\u001f\u007f]/.test(trimmed))
    throw new FilesError(
      "bad_request",
      `"${trimmed}" holds a slash or a control character; a file name may not.`,
    );
  return trimmed;
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** The bytes' SHA-256, hex. */
async function sha256Of(bytes: Uint8Array): Promise<string> {
  return hex(await crypto.subtle.digest("SHA-256", bytes as BufferSource));
}

function view(file: StoredFile): ProjectFile {
  const versions: ProjectFileVersion[] = file.versions.map((stored) => ({
    version: stored.version,
    sizeBytes: stored.sizeBytes,
    sha256: stored.sha256,
    contentType: stored.contentType,
    uploadedBy: stored.uploadedBy,
    uploadedAt: stored.uploadedAt,
    ...(stored.session === undefined ? {} : { session: stored.session }),
    ...(stored.writtenFrom === undefined ? {} : { writtenFrom: stored.writtenFrom }),
  }));
  const [latest] = versions;
  if (latest === undefined) throw new FilesError("unexpected", `${file.name} has no versions.`);
  return { id: file.id, name: file.name, folder: file.folder ?? null, ...latest, versions };
}

/** How deep folders nest, as the doc service allows (`files/folders.rs`). */
export const MAX_FOLDER_DEPTH = 12;
const FOLDER_NOT_FOUND = "That folder was not found in this project.";

/** The folder and its ancestors, nearest first. */
function lineOf(folders: readonly ProjectFolder[], id: string): ProjectFolder[] {
  const line: ProjectFolder[] = [];
  let at = folders.find((folder) => folder.id === id);
  while (at !== undefined && line.length <= MAX_FOLDER_DEPTH * 2) {
    line.push(at);
    const parent = at.parent;
    at = parent === null ? undefined : folders.find((folder) => folder.id === parent);
  }
  return line;
}

/** How many levels a folder and those inside it take. */
function heightOf(folders: readonly ProjectFolder[], id: string): number {
  const inside = folders.filter((folder) => folder.parent === id);
  return 1 + Math.max(0, ...inside.map((folder) => heightOf(folders, folder.id)));
}

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

const GONE = "That file was deleted. It is in the project's Trash, where it can be restored.";
const NOT_FOUND = "That file was not found in this project.";

const inTrash = (file: StoredFile): boolean => file.deletedAt !== undefined;

function trashedView(file: StoredFile): TrashedFile {
  return {
    ...view(file),
    deletedAt: file.deletedAt ?? "",
    deletedBy: file.deletedBy ?? "",
    purgeAfter: file.purgeAfter ?? "",
  };
}

export function createLocalFilesApi(options: LocalFilesOptions): ProjectFilesApi {
  const { store } = options;
  // One change at a time, so two uploads of one name become versions n and n + 1.
  let queue: Promise<unknown> = Promise.resolve();
  const serial = <T>(job: () => Promise<T>): Promise<T> => {
    const next = queue.then(job, job);
    queue = next.catch(() => undefined);
    return next;
  };

  const version = async (number: number, upload: Upload): Promise<StoredVersion> => {
    if (upload.bytes.length > options.maxUploadBytes)
      throw new FilesError(
        "too_large",
        `Files are at most ${String(options.maxUploadBytes)} bytes.`,
      );
    const blob = options.newId();
    await store.putBlob(blob, upload.bytes);
    const digest = await sha256Of(upload.bytes);
    return {
      version: number,
      blob,
      sizeBytes: upload.bytes.length,
      sha256: digest,
      contentType: upload.contentType === "" ? "application/octet-stream" : upload.contentType,
      uploadedBy: upload.uploadedBy,
      uploadedAt: options.now().toISOString(),
      ...(upload.save === undefined ? {} : { session: upload.save.session }),
      ...(upload.save?.from === undefined ? {} : { writtenFrom: upload.save.from }),
    };
  };

  const next = async (file: StoredFile, upload: Upload): Promise<ProjectFile> => {
    // A save back of the latest version's bytes records nothing, as on the doc service (D-062).
    if (upload.save !== undefined && file.versions[0]?.sha256 === (await sha256Of(upload.bytes)))
      return view(file);
    const made = await version((file.versions[0]?.version ?? 0) + 1, upload);
    const updated: StoredFile = {
      ...file,
      versions: [made, ...file.versions],
      updatedAt: made.uploadedAt,
    };
    await store.putFile(updated);
    return view(updated);
  };

  const live = async (projectId: string): Promise<StoredFile[]> =>
    (await store.files(projectId)).filter((file) => !inTrash(file));

  const find = async (projectId: string, fileId: string): Promise<StoredFile> => {
    const found = (await store.files(projectId)).find((file) => file.id === fileId);
    if (found === undefined) throw new FilesError("not_found", NOT_FOUND);
    if (inTrash(found)) throw new FilesError("gone", GONE);
    return found;
  };

  const clash = (
    folders: readonly ProjectFolder[],
    parent: string | null,
    name: string,
    self?: string,
  ) => {
    if (folders.some((f) => f.parent === parent && f.id !== self && sameName(f.name, name)))
      throw new FilesError("conflict", "A folder there has that name already.");
  };

  const folderOf = (folders: readonly ProjectFolder[], id: string): ProjectFolder => {
    const found = folders.find((folder) => folder.id === id);
    if (found === undefined) throw new FilesError("not_found", FOLDER_NOT_FOUND);
    return found;
  };

  const trashed = async (projectId: string, fileId: string): Promise<StoredFile> => {
    const found = (await store.files(projectId)).find((file) => file.id === fileId);
    if (found === undefined || !inTrash(found)) throw new FilesError("not_found", NOT_FOUND);
    return found;
  };

  const purgeFile = async (file: StoredFile): Promise<void> => {
    for (const stored of file.versions) await store.deleteBlob(stored.blob);
    await store.deleteFile(file.id);
  };

  const api: ProjectFilesApi = {
    async folders(projectId) {
      const folders = await store.folders(projectId);
      return [...folders].sort(
        (a, b) =>
          (a.parent ?? "").localeCompare(b.parent ?? "") ||
          a.position - b.position ||
          a.name.localeCompare(b.name),
      );
    },
    createFolder(projectId, name, parent) {
      return serial(async () => {
        const trimmed = checkName(name);
        const folders = await store.folders(projectId);
        if (parent !== null && lineOf(folders, parent).length === 0)
          throw new FilesError("not_found", FOLDER_NOT_FOUND);
        if (parent !== null && lineOf(folders, parent).length >= MAX_FOLDER_DEPTH)
          throw new FilesError(
            "bad_request",
            `Folders nest at most ${String(MAX_FOLDER_DEPTH)} deep.`,
          );
        clash(folders, parent, trimmed);
        const beside = folders.filter((folder) => folder.parent === parent);
        const made: ProjectFolder = {
          id: options.newId(),
          name: trimmed,
          parent,
          position: Math.max(-1, ...beside.map((folder) => folder.position)) + 1,
          createdBy: "",
          createdAt: options.now().toISOString(),
        };
        await store.putFolders(projectId, [...folders, made]);
        return made;
      });
    },
    updateFolder(projectId, folderId, change) {
      return serial(async () => {
        const folders = await store.folders(projectId);
        const current = folderOf(folders, folderId);
        const parent = change.parent === undefined ? current.parent : change.parent;
        if (parent !== current.parent && parent !== null) {
          const line = lineOf(folders, parent);
          if (line.length === 0) throw new FilesError("not_found", FOLDER_NOT_FOUND);
          if (line.some((folder) => folder.id === folderId))
            throw new FilesError(
              "conflict",
              "A folder cannot go inside itself or a folder within it.",
            );
          if (line.length + heightOf(folders, folderId) > MAX_FOLDER_DEPTH)
            throw new FilesError(
              "bad_request",
              `Folders nest at most ${String(MAX_FOLDER_DEPTH)} deep.`,
            );
        }
        const name = change.name === undefined ? current.name : checkName(change.name);
        clash(folders, parent, name, folderId);
        const updated = { ...current, name, parent, position: change.position ?? current.position };
        await store.putFolders(
          projectId,
          folders.map((folder) => (folder.id === folderId ? updated : folder)),
        );
        return updated;
      });
    },
    deleteFolder(projectId, folderId) {
      return serial(async () => {
        const folders = await store.folders(projectId);
        folderOf(folders, folderId);
        const files = await store.files(projectId);
        // Files in the Trash do not fill a folder: they go to the top, as the service does.
        if (
          folders.some((folder) => folder.parent === folderId) ||
          files.some((file) => file.folder === folderId && !inTrash(file))
        )
          throw new FilesError(
            "conflict",
            "Only an empty folder can be deleted. Move or delete what is in it first.",
          );
        for (const file of files.filter((held) => held.folder === folderId))
          await store.putFile({ ...file, folder: null });
        await store.putFolders(
          projectId,
          folders.filter((folder) => folder.id !== folderId),
        );
      });
    },
    moveFile(projectId, fileId, folder) {
      return serial(async () => {
        if (folder !== null) folderOf(await store.folders(projectId), folder);
        const moved = { ...(await find(projectId, fileId)), folder };
        await store.putFile(moved);
        return view(moved);
      });
    },
    async list(projectId) {
      const files = await live(projectId);
      return files
        .sort((a, b) => (a.updatedAt === b.updatedAt ? 0 : a.updatedAt < b.updatedAt ? 1 : -1))
        .map(view);
    },
    upload(projectId, name, upload) {
      return serial(async () => {
        const trimmed = checkName(name);
        const key = trimmed.toLowerCase();
        const existing = (await live(projectId)).find((file) => file.name.toLowerCase() === key);
        if (existing !== undefined) return next(existing, upload);
        const first = await version(1, upload);
        const file: StoredFile = {
          id: options.newId(),
          projectId,
          name: trimmed,
          versions: [first],
          updatedAt: first.uploadedAt,
        };
        await store.putFile(file);
        return view(file);
      });
    },
    addVersion(projectId, fileId, upload) {
      return serial(async () => next(await find(projectId, fileId), upload));
    },
    async uploadFile(projectId, target, picked, upload) {
      // Kept in this browser, so the file is read into memory whole; progress is just the end.
      upload.onProgress?.(0, picked.size);
      const whole = {
        bytes: new Uint8Array(await picked.arrayBuffer()),
        contentType: upload.contentType,
        uploadedBy: upload.uploadedBy,
      };
      const saved =
        "name" in target
          ? await api.upload(projectId, target.name, whole)
          : await api.addVersion(projectId, target.fileId, whole);
      upload.onProgress?.(picked.size, picked.size);
      return saved;
    },
    downloadLink() {
      return Promise.resolve(undefined);
    },
    rasterEndpoint() {
      return undefined;
    },
    dxfOf() {
      return Promise.reject(new FilesError("unavailable", DWG_NEEDS_SERVICE));
    },
    saveDwg() {
      return Promise.reject(new FilesError("unavailable", DWG_NEEDS_SERVICE));
    },
    toDwg() {
      return Promise.reject(new FilesError("unavailable", DWG_NEEDS_SERVICE));
    },
    remove(projectId, fileId, deletedBy) {
      return serial(async () => {
        const file = await find(projectId, fileId);
        const now = options.now();
        const deleted: StoredFile = {
          ...file,
          deletedAt: now.toISOString(),
          deletedBy,
          purgeAfter: new Date(now.getTime() + options.trashRetentionMs).toISOString(),
        };
        await store.putFile(deleted);
        return trashedView(deleted);
      });
    },
    trash(projectId) {
      return serial(async () => {
        const now = options.now().toISOString();
        const files = (await store.files(projectId)).filter(inTrash);
        const kept: StoredFile[] = [];
        for (const file of files) {
          if ((file.purgeAfter ?? "") <= now) await purgeFile(file);
          else kept.push(file);
        }
        return kept
          .sort((a, b) => ((a.deletedAt ?? "") < (b.deletedAt ?? "") ? 1 : -1))
          .map(trashedView);
      });
    },
    restore(projectId, fileId) {
      return serial(async () => {
        const file = await trashed(projectId, fileId);
        const key = file.name.toLowerCase();
        const holder = (await live(projectId)).find((other) => other.name.toLowerCase() === key);
        if (holder !== undefined)
          throw new FilesError(
            "conflict",
            `A file named ${holder.name} is in the project now. Delete that one first to restore this one.`,
          );
        const restored: StoredFile = {
          id: file.id,
          projectId: file.projectId,
          name: file.name,
          versions: file.versions,
          updatedAt: options.now().toISOString(),
        };
        await store.putFile(restored);
        return view(restored);
      });
    },
    purge(projectId, fileId) {
      return serial(async () => purgeFile(await trashed(projectId, fileId)));
    },
    async download(projectId, fileId, wanted) {
      const file = await find(projectId, fileId);
      const chosen =
        wanted === undefined ? file.versions[0] : file.versions.find((v) => v.version === wanted);
      const bytes = chosen === undefined ? undefined : await store.blob(chosen.blob);
      if (bytes === undefined) throw new FilesError("not_found", "That version was not found.");
      return bytes;
    },
  };
  return { ...api, ...createLocalIssues({ list: api.list, now: options.now }) };
}

/** A store in memory, for tests. */
export function memoryStore(): LocalStore {
  const files = new Map<string, StoredFile>();
  const blobs = new Map<string, Uint8Array>();
  const folders = new Map<string, readonly ProjectFolder[]>();
  return {
    folders: (projectId) => Promise.resolve([...(folders.get(projectId) ?? [])]),
    putFolders: (projectId, list) => {
      folders.set(projectId, [...list]);
      return Promise.resolve();
    },
    files: (projectId) =>
      Promise.resolve([...files.values()].filter((f) => f.projectId === projectId)),
    putFile: (file) => {
      files.set(file.id, file);
      return Promise.resolve();
    },
    blob: (key) => Promise.resolve(blobs.get(key)),
    putBlob: (key, bytes) => {
      blobs.set(key, bytes.slice());
      return Promise.resolve();
    },
    deleteFile: (id) => {
      files.delete(id);
      return Promise.resolve();
    },
    deleteBlob: (key) => {
      blobs.delete(key);
      return Promise.resolve();
    },
  };
}
